/**
 * Tracks project ids the current dashboard session just deleted, so the
 * deleted-project 404 recovery (useProjectNotFoundRedirect) can tell an
 * INTENTIONAL self-delete apart from a STALE tab whose project was deleted
 * elsewhere.
 *
 * Why this exists: deleting a project invalidates the broad ['projects'] query
 * key, which prefix-matches the currently mounted useProject(deletedId) query
 * (['projects', id]). That query refetches GET /projects/:id → 404, which is the
 * exact signal useProjectNotFoundRedirect was built to catch for a stale tab
 * (incident 2026-07-13). Firing "This project no longer exists." on a project
 * the user just deliberately deleted reads like a dead-link click and stacks a
 * second, contradictory toast on top of the delete handler's own
 * "Project deleted" success. Marking the id here lets the recovery suppress its
 * toast (it still redirects, as a safety net) only for a self-delete.
 *
 * A short TTL bounds the map (one entry per delete over a long session) and is
 * far longer than the ms-scale gap between the delete succeeding and the doomed
 * refetch 404ing, so it never misses the race it exists to cover.
 */

const DEFAULT_TTL_MS = 30_000;

// id -> epoch ms after which the mark is stale. Module-level so the delete
// mutation and the recovery hook (different components) share one source.
const deletedAt = new Map<string, number>();

/** Record that this session just deleted `projectId`. */
export function markProjectDeleted(projectId: string, ttlMs: number = DEFAULT_TTL_MS): void {
  if (!projectId) return;
  deletedAt.set(projectId, Date.now() + ttlMs);
}

/**
 * True if `projectId` was deleted by this session within the TTL. Prunes the
 * entry lazily when it has expired so the map never grows unbounded.
 */
export function wasProjectRecentlyDeleted(projectId: string | undefined | null): boolean {
  if (!projectId) return false;
  const expiry = deletedAt.get(projectId);
  if (expiry === undefined) return false;
  if (Date.now() > expiry) {
    deletedAt.delete(projectId);
    return false;
  }
  return true;
}

/** Test-only: clear all marks so cases don't leak state into each other. */
export function __resetRecentlyDeletedProjects(): void {
  deletedAt.clear();
}
